import api from '@/helpers/api_helper';
import { apiUrls } from '@/constant/configure';

export type Vendor = {
    id: string;
    nickname: string;
    email?: string;
    imageUrl?: string;
    vendorPath?: string;
    description?: string;
    vendorBusinessInfo?: {
        storeLocation?: string;
        storeName?: string;
    };
};

export type ProductOption = {
    id: string;
    optionName: string;
    inventory: number;
    supplyPrice: number;
    marketPrice: number;
    discountMethod: string;
    discountValue: number;
    productCharge: number;
    isPublic: string;
    createdAt: string;
    isActivated: string;
    marginRate: number;
    productCode: string | null;
    optionImageUrl: string | null;
    color: string | null;
};

export type Product = {
    id: string;
    vendorId: string;
    representProductId: string;
    categoryId: string;
    groupTitle: string;
    groupTitleI18n?: Record<string, string>;
    representImageUrl: string;
    tags: [];
    description: string;
    descriptionI18n?: Record<string, string>;
    salesPolicyDescription: string;
    createdAt: string;
    groupImageUrl: string[];
    optionShowOrder: string[];
    productInfo: {
        product_id: string;
        options: {
            optionKey?: string;
            option_key?: string;
            optionValue?: string;
            option_value?: string;
        }[];
    }[];
    productDocuments: {
        list: string[];
        name: string;
        listId: string;
    }[];
    isPublic: string;
    shippingReturnAddressBookId: string;
    onlineMinPriceLink: string;
    isPrimaryProduct: string;
    followerCutoffMin: number;
    manufacturer: string;
    countryOfOrigin: string;
    manufactureDate: string;
    afterServiceManager: string;
    afterServicePhone: string;
    categoryTags: string[];
    productCode: string | null;
    isSetProduct: string;
    setSelectionCount: number;
    optionSelectorType: string;
    matchingProducts: ProductOption[];
    fabricInfo?: {
        lining?: string;
        thickness?: string;
        elasticity?: string;
        seeThrough?: string;
    };
    washInfo?: Record<string, string | string[]>; // 단일 값 또는 배열 모두 지원
    materialInfo?: Array<any> | null;
    colorNameInfo?: { [key: string]: string };
    sizeInfo?: { size: string; [key: string]: string }[];
    sizeType?: string;
    useThumbnailInDetail?: 't' | 'f' | null;
    thumbnailInDetailPosition?: 'top' | 'bottom' | null;
    matchingSponsorshipCondition?: {
        id: string;
        conditionType: string | null;
        condition: string;
        managerName: string;
        managerPhone: string;
        managerEmail: string;
        matchingProductGroupId: string;
        createdAt: string;
        shippingReturnAddress?: {
            recipientAddressDetail: string;
        };
    };
    vendor?: Vendor;
    seasonIds?: string[];
    styleIds?: string[];
    patternIds?: string[];
    fit?: { id: string; code: string; name: string };
    sheerness?: { id: string; code: string; name: string };
    stretch?: { id: string; code: string; name: string };
    thickness?: { id: string; code: string; name: string };
    lining?: { id: string; code: string; name: string };
    detailIds?: string[];
};

export type WashInfo = Record<string, string | string[]>; // 단일 값 또는 배열 모두 지원

export type Category = {
    categoryId: string;
    categoryName: string;
    imageUrl: string;
    parentCategoryId: string;
    sortOrder: number;
    wholeCategoryId: string;
    wholeCategoryName: string;
};

type ProductListResponse = {
    data: Product[];
    count: number;
    page: number;
    pageCount: number;
    total: number;
};

// 참고 프로젝트와 동일한 엔드포인트 사용
export const getProductList = async ({ page, limit, filter }: { page: number; limit: number; filter: string }) => {
    const baseURL = apiUrls.matching;
    const url = `/web-vendor/products?${filter}`;
    const { data } = await api.get(url, {
        baseURL,
        params: { page, limit },
    });
    return data as ProductListResponse;
};

// 간소화 list
export const getProductListSimple = async ({
    page,
    limit,
    filter,
}: {
    page: number;
    limit: number;
    filter: string;
}) => {
    const baseURL = apiUrls.matching;
    const url = `/web-vendor/products/list?${filter}`;
    const { data } = await api.get(url, {
        baseURL,
        params: { page, limit },
    });
    return data as ProductListResponse;
};

export const getProductDetail = async ({ productId }: { productId: string }) => {
    const baseURL = apiUrls.matching;
    const url = `/web-vendor/products/${productId}`;
    const { data } = await api.get(url, { baseURL });
    return data as Product;
};

export const getCategories = async (type?: 'MAIN' | 'SUB'): Promise<Category[]> => {
    const baseURL = apiUrls.matching;
    const categoryType = type || 'MAIN';
    const url = `/common/category?type=${categoryType}`;
    const { data } = await api.get(url, { baseURL });
    return data?.items || [];
};

export const getSubCategories = async (parentCategoryId: string): Promise<Category[]> => {
    const baseURL = apiUrls.matching;
    const url = `/common/category?type=SUB&parentCategoryId=${parentCategoryId}`;
    const { data } = await api.get(url, { baseURL });
    return data?.items || [];
};

export type DimEntity = {
    id: string;
    code: string | null;
    name: string;
    sortOrder: number;
};

export type DimEntitiesResponse = {
    dimFit: DimEntity[];
    dimPattern: DimEntity[];
    dimSeason: DimEntity[];
    dimSheerness: DimEntity[];
    dimStretch: DimEntity[];
    dimStyle: DimEntity[];
    dimThickness: DimEntity[];
};

export const getDimEntities = async (): Promise<DimEntitiesResponse> => {
    const baseURL = apiUrls.matching;
    const url = '/web-vendor/products/dim-entities';
    const { data } = await api.get(url, { baseURL });
    return data as DimEntitiesResponse;
};
